import React from "react";
import { Typography } from "@mui/material";
import DocumentUpload from "@components/FileUpload/FileUpload";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import SingleFile from "@components/FileUpload/SingleFile";
import { useUploadStore, type UploadEntry } from "@components/FileUpload/uploadStore";
import FormItem from "@components/FormItem/FormItem";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import { useNotification } from "@context/NotificationContext";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  approveLicenseSchema,
  COMPANY_ID_LENGTH,
  MAX_TEXT_LENGTH,
  type ApproveLicenseFormData,
  type ApproveLicenseSchema,
} from "./approveLicenseSchema";

interface ApproveLicenseDrawerProps {
  isOpen: boolean;
  onClose: (drawerName: string) => void;
  onSubmit?: (payload: {
    startDate: string;
    expiryDate: string;
    numberOfAccountsAllowed: number;
    companyId: string;
    approvalNotes?: string;
  }) => void;
  refOrderNo: number;
  tradingLicenseValidityDate?: string;
  vendorLicenseExpiryDate?: string;
  onDocumentUpload?: (file: File) => void;
  isUploading?: boolean;
  isSubmitting?: boolean;
  prefilledCompanyId?: string;
}

const EMPTY: UploadEntry[] = [];

type ApproveFormData = ApproveLicenseFormData;

const F = {
  START_DATE: "startDate",
  EXPIRY_DATE: "expiryDate",
  NUMBER_OF_ACCOUNTS_ALLOWED: "numberOfAccountsAllowed",
  COMPANY_ID: "companyId",
  APPROVAL_NOTES: "approvalNotes",
} as const;

const toISODate = (d: Date) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const parseDate = (value?: string) => {
  if (!value) return null;
  const date = new Date(`${value.slice(0, 10)}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
};

const getDefaultAccessDates = (vendorLicenseExpiryDate?: string) => {
  const from = new Date();
  from.setHours(0, 0, 0, 0);

  const licenseExpiryDate = parseDate(vendorLicenseExpiryDate);
  const until = licenseExpiryDate ?? new Date(from);
  until.setDate(until.getDate() + 30);

  return {
    startDate: toISODate(from),
    expiryDate: toISODate(until),
  };
};

const ApproveLicenseDrawer: React.FC<ApproveLicenseDrawerProps> = ({
  isOpen,
  onClose,
  onSubmit,
  refOrderNo,
  tradingLicenseValidityDate,
  vendorLicenseExpiryDate,
  onDocumentUpload,
  isUploading,
  isSubmitting,
  prefilledCompanyId,
}) => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const normalizedPrefilledCompanyId = prefilledCompanyId?.trim().toUpperCase() ?? "";
  const isRenewalApproval = Boolean(normalizedPrefilledCompanyId);

  const effectiveVendorLicenseExpiryDate = vendorLicenseExpiryDate ?? tradingLicenseValidityDate;

  const schema = React.useMemo(
    () => approveLicenseSchema(t, { requireCompanyId: !isRenewalApproval }),
    [isRenewalApproval, t],
  );

  const form = useForm<ApproveFormData, unknown, ApproveLicenseSchema>({
    resolver: zodResolver(schema),
    mode: "onChange",
    defaultValues: {
      ...getDefaultAccessDates(effectiveVendorLicenseExpiryDate),
      numberOfAccountsAllowed: 1,
      companyId: prefilledCompanyId ?? "",
      approvalNotes: "",
    },
  });

  const { handleSubmit, reset } = form;

  const approvalFiles = useUploadStore((s) => s.filesByKey.licenseApprovalAttachment ?? EMPTY);
  const isApprovalUploadInFlight = useUploadStore((s) =>
    s.isUploadInFlight("licenseApprovalAttachment"),
  );
  const setFilesForKey = useUploadStore((s) => s.setFilesForKey);
  const clearFilesForKey = useUploadStore((s) => s.clearFilesForKey);

  const fileProfiles = useFileUploadProfiles();
  const approvalProfile = fileProfiles.sideDrawerApprovalFile;

  const allowedExtensions = approvalProfile.format.map((ext) =>
    ext.startsWith(".") ? ext.toLowerCase() : `.${ext.toLowerCase()}`,
  );

  const resetForm = React.useCallback(() => {
    reset({
      ...getDefaultAccessDates(effectiveVendorLicenseExpiryDate),
      numberOfAccountsAllowed: 1,
      companyId: prefilledCompanyId ?? "",
      approvalNotes: "",
    });
    clearFilesForKey("licenseApprovalAttachment");
  }, [clearFilesForKey, reset, effectiveVendorLicenseExpiryDate, prefilledCompanyId]);

  const prevOpenRef = React.useRef(isOpen);
  React.useEffect(() => {
    if (prevOpenRef.current && !isOpen) {
      resetForm();
    }
    prevOpenRef.current = isOpen;
  }, [isOpen, resetForm]);

  const handleClose = () => {
    onClose("approveDrawer");
    resetForm();
  };

  const handleCancel = () => {
    onClose("approveDrawer");
    resetForm();
  };

  const handleUpload = (newFiles: File[]) => {
    if (newFiles.length > 1) return;
    const file = newFiles[0];
    if (!file) return;

    setFilesForKey("licenseApprovalAttachment", [file]);
    onDocumentUpload?.(file);
  };

  const handleSubmitForm = (data: ApproveLicenseSchema) => {
    const normalizedCompanyId = isRenewalApproval
      ? normalizedPrefilledCompanyId
      : data.companyId.trim().toUpperCase();

    onSubmit?.({
      startDate: data.startDate,
      expiryDate: data.expiryDate,
      numberOfAccountsAllowed: 1,
      companyId: normalizedCompanyId,
      approvalNotes: data.approvalNotes?.trim() ? data.approvalNotes.trim() : undefined,
    });
  };

  const isAttachmentUploading = Boolean(isUploading || isApprovalUploadInFlight);

  const handleDrawerSubmit = () => {
    if (isAttachmentUploading) {
      notify({
        title: t("common.notification.please-wait-for-upload"),
        type: "warning",
      });
      return;
    }

    void handleSubmit(handleSubmitForm)();
  };

  if (!isOpen) return null;

  return (
    <div data-testid="approve-license-drawer-form" className="min-w-[400px]">
      <SideDrawer
        isOpen={isOpen}
        title={t("licensing.vendor-license-approve-title")}
        onClose={handleClose}
        onCancel={handleCancel}
        submitLabel={t("licensing.approve-button")}
        onSubmit={handleDrawerSubmit}
        isSubmitting={Boolean(isSubmitting)}
      >
        <Typography sx={{ px: 3, pb: 2 }} data-testid="approve-license-subtitle">
          {t("licensing.vendor-license-approve-subtitle")}
          <br />
          <span style={{ fontWeight: 600 }}>{refOrderNo}.</span>
        </Typography>

        <div className="flex-1 w-[400px] py-2">
          <div className="flex flex-col gap-2 px-6">
            <FormProvider {...form}>
              <FormItem<ApproveFormData, typeof F.START_DATE>
                required
                name={F.START_DATE}
                type="date-picker"
                label={t("licensing.access-enabled-from")}
                timePicker={false}
                testId="approve-access-enabled-from"
                dateConstraint="not-past"
                disabled
              />

              <FormItem<ApproveFormData, typeof F.EXPIRY_DATE>
                required
                name={F.EXPIRY_DATE}
                type="date-picker"
                label={t("licensing.access-enabled-until")}
                timePicker={false}
                testId="approve-access-enabled-until"
                dateConstraint="not-past"
              />

              <FormItem<ApproveFormData, typeof F.NUMBER_OF_ACCOUNTS_ALLOWED>
                required
                name={F.NUMBER_OF_ACCOUNTS_ALLOWED}
                type="number"
                label={t("licensing.no-of-accounts-allowed")}
                testId="approve-no-of-accounts-allowed"
                disabled
                defaultValue={1}
              />

              <FormItem<ApproveFormData, typeof F.COMPANY_ID>
                required
                name={F.COMPANY_ID}
                type="text"
                label={t("licensing.company-id")}
                testId="approve-company-id"
                enforceUppercase
                disabled={isRenewalApproval}
                formatOnChange={
                  isRenewalApproval
                    ? undefined
                    : (e) => {
                        const input = e.target.value;
                        const sanitized = input
                          .toUpperCase()
                          .replace(/[^A-Z]/g, "")
                          .slice(0, COMPANY_ID_LENGTH);
                        if (sanitized !== input) {
                          e.target.value = sanitized;
                        }
                      }
                }
                slotProps={{
                  htmlInput: {
                    maxLength: isRenewalApproval ? undefined : COMPANY_ID_LENGTH,
                    readOnly: isRenewalApproval,
                  },
                }}
              />

              <FormItem<ApproveFormData, typeof F.APPROVAL_NOTES>
                name={F.APPROVAL_NOTES}
                type="textarea"
                label={t("licensing.notes")}
                testId="approve-notes"
                maxChars={MAX_TEXT_LENGTH}
                showCharCounter
                minRows={4}
              />

              <DocumentUpload
                label={t("licensing.attachments")}
                testId="license-approve-attachment-upload"
                name="licenseApprovalAttachment"
                maxFiles={1}
                allowedExtensions={allowedExtensions}
                onUpload={handleUpload}
                conditions={approvalProfile}
                files={approvalFiles}
                setFiles={(next) => setFilesForKey("licenseApprovalAttachment", next)}
                uploadDescription={t("upload.dragOrDropDescription")}
              />

              {approvalFiles[0] && (
                <SingleFile
                  file={approvalFiles[0]}
                  fieldName="licenseApprovalAttachment"
                  setFiles={() => clearFilesForKey("licenseApprovalAttachment")}
                  removeFn={() => clearFilesForKey("licenseApprovalAttachment")}
                  isLoading={isAttachmentUploading}
                />
              )}
            </FormProvider>
          </div>
        </div>
      </SideDrawer>
    </div>
  );
};

export default ApproveLicenseDrawer;
